
// src/pages/EstimatorPage.tsx
import { useState, useEffect, FormEvent } from 'react';
import {
  Car,
  Calendar,
  Gauge,
  Sparkles,
  TrendingUp,
  CheckCircle,
  Lightbulb,
  ExternalLink,
  Loader2,
  AlertCircle,
  Fuel,
  Shapes,
  Cog,
  X,
  Phone,
  Mail,
  User,
  MessageSquare,
  Zap,
  Clock,
} from 'lucide-react';

import { estimatorApi, EstimationResult, EstimatorFormData } from '../lib/estimatorApi';

// Interface pour les données du formulaire de contact
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
}

// Typing animation hook
const useTypingEffect = (messages: string[], typingSpeed = 50, deletingSpeed = 30, pauseTime = 1500) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const currentMessage = messages[currentMessageIndex];
    
    if (isPaused) {
      const pauseTimeout = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseTime);
      return () => clearTimeout(pauseTimeout);
    }

    if (isDeleting) {
      if (currentText.length > 0) {
        const deleteTimeout = setTimeout(() => {
          setCurrentText(currentText.substring(0, currentText.length - 1));
        }, deletingSpeed);
        return () => clearTimeout(deleteTimeout);
      } else {
        setIsDeleting(false);
        setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
      }
    } else {
      if (currentText.length < currentMessage.length) {
        const typeTimeout = setTimeout(() => {
          setCurrentText(currentMessage.substring(0, currentText.length + 1));
        }, typingSpeed);
        return () => clearTimeout(typeTimeout);
      } else {
        setIsPaused(true);
      }
    }
  }, [currentText, isDeleting, isPaused, currentMessageIndex, messages, typingSpeed, deletingSpeed, pauseTime]);

  return currentText;
};

// Generate browser fingerprint
const generateBrowserFingerprint = async (): Promise<string> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    canvas.toDataURL(),
  ].join('|');
  
  // Simple hash function to create a consistent fingerprint
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(16);
};

// Get user IP address
const getUserIP = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    return 'unknown';
  }
};

// --- Base de données de voitures (inchangée) ---
const vehicleTypes = [
  'Citadine',
  'Berline',
  'SUV / 4x4',
  'Utilitaire',
  'Coupé / Cabriolet',
  'Monospace',
  'Autre',
];

const carDatabase: Record<string, Array<{ name: string; type: string }>> = {
  'ALFA ROMEO': [
    { name: 'Mito', type: 'Citadine' },
    { name: 'Giulia', type: 'Berline' },
    { name: 'Giulietta', type: 'Berline' },
    { name: 'Stelvio', type: 'SUV / 4x4' },
    { name: 'Tonale', type: 'SUV / 4x4' },
  ],
  AUDI: [
    { name: 'A1', type: 'Citadine' },
    { name: 'A3', type: 'Berline' },
    { name: 'A4', type: 'Berline' },
    { name: 'A5', type: 'Coupé / Cabriolet' },
    { name: 'A6', type: 'Berline' },
    { name: 'A7', type: 'Berline' },
    { name: 'A8', type: 'Berline' },
    { name: 'Q2', type: 'SUV / 4x4' },
    { name: 'Q3', type: 'SUV / 4x4' },
    { name: 'Q5', type: 'SUV / 4x4' },
    { name: 'Q7', type: 'SUV / 4x4' },
    { name: 'Q8', type: 'SUV / 4x4' },
  ],
  BAIC: [
    { name: 'Kenbo S2', type: 'SUV / 4x4' },
    { name: 'Kenbo S3', type: 'SUV / 4x4' },
  ],
  BESTUNE: [
    { name: 'T77', type: 'SUV / 4x4' },
  ],
  BMW: [
    { name: 'Série 1', type: 'Berline' },
    { name: 'Série 2', type: 'Coupé / Cabriolet' },
    { name: 'Série 3', type: 'Berline' },
    { name: 'Série 4', type: 'Coupé / Cabriolet' },
    { name: 'Série 5', type: 'Berline' },
    { name: 'Série 7', type: 'Berline' },
    { name: 'X1', type: 'SUV / 4x4' },
    { name: 'X2', type: 'SUV / 4x4' },
    { name: 'X3', type: 'SUV / 4x4' },
    { name: 'X4', type: 'SUV / 4x4' },
    { name: 'X5', type: 'SUV / 4x4' },
    { name: 'X6', type: 'SUV / 4x4' },
  ],
  BYD: [
    { name: 'Atto3', type: 'SUV / 4x4' },
    { name: 'Dolphin', type: 'Citadine' },
    { name: 'F3', type: 'Berline' },
    { name: 'King', type: 'Berline' },
    { name: 'Song', type: 'SUV / 4x4' },
    { name: 'Tang', type: 'SUV / 4x4' },
  ],
  CHANGAN: [
    { name: 'Hunter', type: 'Utilitaire' },
    { name: 'Star Truck', type: 'Utilitaire' },
    { name: 'Star Van', type: 'Utilitaire' },
  ],
  CHERY: [
    { name: 'Arrizo 5', type: 'Berline' },
    { name: 'Arrizo 8', type: 'Berline' },
    { name: 'Tiggo 1', type: 'SUV / 4x4' },
    { name: 'Tiggo 2', type: 'SUV / 4x4' },
    { name: 'Tiggo 3', type: 'SUV / 4x4' },
    { name: 'Tiggo 4', type: 'SUV / 4x4' },
    { name: 'Tiggo 7', type: 'SUV / 4x4' },
    { name: 'Tiggo 8', type: 'SUV / 4x4' },
    { name: 'QQ', type: 'Citadine' },
  ],
  CHEVROLET: [
    { name: 'Equinox', type: 'SUV / 4x4' },
    { name: 'Groove', type: 'SUV / 4x4' },
    { name: 'Cruze', type: 'Berline' },
    { name: 'Sonic', type: 'Citadine' },
    { name: 'Aveo', type: 'Berline' },
    { name: 'Spark', type: 'Citadine' },
    { name: 'Captiva', type: 'SUV / 4x4' },
  ],
  CITROËN: [
    { name: 'Berlingo Multispace', type: 'Monospace' },
    { name: 'Berlingo Van', type: 'Utilitaire' },
    { name: 'C1', type: 'Citadine' },
    { name: 'C3', type: 'Citadine' },
    { name: 'C3 Picasso', type: 'Monospace' },
    { name: 'C3 Air-cross', type: 'SUV / 4x4' },
    { name: 'C-Elysée', type: 'Berline' },
    { name: 'C4', type: 'Berline' },
    { name: 'C4 X', type: 'Berline' },
    { name: 'C4 Cactus', type: 'Berline' },
    { name: 'C5', type: 'Berline' },
    { name: 'C5 Air-cross', type: 'SUV / 4x4' },
    { name: 'C4 Picasso', type: 'Monospace' },
    { name: 'C6', type: 'Berline' },
    { name: 'Jumpy Fourgon', type: 'Utilitaire' },
    { name: 'Jumpy Combi', type: 'Monospace' },
    { name: 'Jumper', type: 'Utilitaire' },
    { name: 'Nemo', type: 'Utilitaire' },
    { name: 'Nemo Multispace', type: 'Monospace' },
  ],
  CUPRA: [
    { name: 'Leon', type: 'Berline' },
    { name: 'Formentor', type: 'SUV / 4x4' },
    { name: 'Terramar', type: 'SUV / 4x4' },
  ],
  DACIA: [
    { name: 'Logan', type: 'Berline' },
    { name: 'Sandero Stepway', type: 'Citadine' },
    { name: 'Dokker', type: 'Utilitaire' },
    { name: 'Sandero', type: 'Citadine' },
    { name: 'Duster', type: 'SUV / 4x4' },
    { name: 'Logan MCV', type: 'Berline' },
  ],
  DFSK: [
    { name: 'Glory 580', type: 'SUV / 4x4' },
    { name: 'K01H', type: 'Utilitaire' },
    { name: 'K02S', type: 'Utilitaire' },
    { name: 'K01S', type: 'Utilitaire' },
    { name: 'Glory 500', type: 'SUV / 4x4' },
  ],
  DONGFENG: [
    { name: 'AX4', type: 'SUV / 4x4' },
    { name: 'Box', type: 'Citadine' },
    { name: 'Rich 6', type: 'Utilitaire' },
    { name: 'Shine', type: 'Berline' },
    { name: 'S50', type: 'Berline' },
    { name: 'SX3', type: 'SUV / 4x4' },
    { name: 'T5 EVO', type: 'SUV / 4x4' },
  ],
  DS: [
    { name: 'DS7', type: 'SUV / 4x4' },
    { name: 'DS3', type: 'Citadine' },
    { name: 'DS4', type: 'Berline' },
    { name: 'DS5', type: 'Berline' },
  ],
  FAW: [
    { name: 'Besturn X40', type: 'SUV / 4x4' },
  ],
  FIAT: [
    { name: 'Doblo', type: 'Utilitaire' },
    { name: 'Punto', type: 'Citadine' },
    { name: 'Linea', type: 'Berline' },
    { name: '500', type: 'Citadine' },
    { name: 'Ducato', type: 'Utilitaire' },
    { name: 'Tipo', type: 'Berline' },
    { name: 'Panda', type: 'Citadine' },
    { name: 'Qubo', type: 'Utilitaire' },
    { name: 'Fiorino', type: 'Utilitaire' },
    { name: 'Fiat 500 X', type: 'SUV / 4x4' },
  ],
  FODAY: [
    { name: 'F22 SC', type: 'Utilitaire' },
    { name: 'F22 DC', type: 'Utilitaire' },
  ],
  FORD: [
    { name: 'Everest', type: 'SUV / 4x4' },
    { name: 'KA', type: 'Citadine' },
    { name: 'Ranger', type: 'Utilitaire' },
    { name: 'Fiesta', type: 'Citadine' },
    { name: 'Ecosport', type: 'SUV / 4x4' },
    { name: 'Focus', type: 'Berline' },
    { name: 'Transit', type: 'Utilitaire' },
    { name: 'Kuga', type: 'SUV / 4x4' },
    { name: 'Fusion', type: 'Berline' },
    { name: 'Mustang', type: 'Coupé / Cabriolet' },
  ],
  FOTON: [
    { name: 'Tunland G7 SC', type: 'Utilitaire' },
    { name: 'Tunland G7 DC', type: 'Utilitaire' },
  ],
  GAC: [
    { name: 'GA4', type: 'Berline' },
    { name: 'GS3', type: 'SUV / 4x4' },
    { name: 'GS4', type: 'SUV / 4x4' },
    { name: 'EMKOO', type: 'SUV / 4x4' },
    { name: 'EMZOOM', type: 'SUV / 4x4' },
  ],
  GEELY: [
    { name: 'COOLRAY', type: 'SUV / 4x4' },
    { name: 'GX3', type: 'SUV / 4x4' },
    { name: 'GC6', type: 'Berline' },
    { name: 'Azkarra', type: 'SUV / 4x4' },
  ],
  GMW: [
    { name: 'Poer', type: 'Utilitaire' },
    { name: 'M4', type: 'SUV / 4x4' },
    { name: 'C30', type: 'Berline' },
    { name: 'H5', type: 'SUV / 4x4' },
    { name: 'Wingle 6', type: 'Utilitaire' },
    { name: 'Wingle 5', type: 'Utilitaire' },
    { name: 'Jolion', type: 'SUV / 4x4' },
    { name: 'Haval H2', type: 'SUV / 4x4' },
    { name: 'Haval H6', type: 'SUV / 4x4' },
    { name: 'Haval H8', type: 'SUV / 4x4' },
    { name: 'Haval H9', type: 'SUV / 4x4' },
  ],
  HONDA: [
    { name: 'Amaze', type: 'Berline' },
    { name: 'Civic', type: 'Berline' },
    { name: 'Brio', type: 'Citadine' },
    { name: 'Jazz', type: 'Citadine' },
    { name: 'City', type: 'Berline' },
    { name: 'HR-V', type: 'SUV / 4x4' },
    { name: 'Accord', type: 'Berline' },
    { name: 'CR-V', type: 'SUV / 4x4' },
    { name: 'ZR-V', type: 'SUV / 4x4' },
  ],
  HUANGHAI: [
    { name: 'N3', type: 'Utilitaire' },
  ],
  HYUNDAI: [
    { name: 'Tucson', type: 'SUV / 4x4' },
    { name: 'Kona', type: 'SUV / 4x4' },
    { name: 'Venue', type: 'SUV / 4x4' },
    { name: 'Bayon', type: 'SUV / 4x4' },
    { name: 'Ioniq 5', type: 'SUV / 4x4' },
    { name: 'Palisade', type: 'SUV / 4x4' },
    { name: 'Staria', type: 'Monospace' },
    { name: 'Grand i10', type: 'Citadine' },
    { name: 'Grand i10 Sedan', type: 'Berline' },
    { name: 'i20', type: 'Citadine' },
    { name: 'Accent', type: 'Berline' },
    { name: 'i30', type: 'Berline' },
    { name: 'Elantra', type: 'Berline' },
    { name: 'Veloster', type: 'Coupé / Cabriolet' },
    { name: 'IX 35', type: 'SUV / 4x4' },
    { name: 'H1', type: 'Utilitaire' },
    { name: 'Santa Fe', type: 'SUV / 4x4' },
    { name: 'Creta', type: 'SUV / 4x4' },
  ],
  ISUZU: [
    { name: 'D-Max SC', type: 'Utilitaire' },
    { name: 'D-Max DC', type: 'Utilitaire' },
  ],
  IVECO: [
    { name: 'Daily SC', type: 'Utilitaire' },
    { name: 'Daily DC', type: 'Utilitaire' },
  ],
  JAC: [
    { name: 'JAC T8 Pro DC', type: 'Utilitaire' },
  ],
  JAGUAR: [
    { name: 'E-PACE', type: 'SUV / 4x4' },
    { name: 'XF', type: 'Berline' },
    { name: 'XJ', type: 'Berline' },
    { name: 'F-Type', type: 'Coupé / Cabriolet' },
    { name: 'XE', type: 'Berline' },
    { name: 'F-PACE', type: 'SUV / 4x4' },
  ],
  JEEP: [
    { name: 'Compass', type: 'SUV / 4x4' },
    { name: 'Wrangler', type: 'SUV / 4x4' },
    { name: 'Cherokee', type: 'SUV / 4x4' },
    { name: 'Renegade', type: 'SUV / 4x4' },
    { name: 'Grand Cherokee', type: 'SUV / 4x4' },
  ],
  JETOUR: [
    { name: 'Dashing', type: 'SUV / 4x4' },
    { name: 'X70 Plus', type: 'SUV / 4x4' },
    { name: 'T2', type: 'SUV / 4x4' },
  ],
  JMC: [
    { name: 'Vigus SC', type: 'Utilitaire' },
    { name: 'Vigus DC', type: 'Utilitaire' },
  ],
  KIA: [
    { name: 'Seltos', type: 'SUV / 4x4' },
    { name: 'Xceed', type: 'SUV / 4x4' },
    { name: 'Niro', type: 'SUV / 4x4' },
    { name: 'Soul', type: 'SUV / 4x4' },
    { name: 'Stonic', type: 'SUV / 4x4' },
    { name: 'EV6', type: 'SUV / 4x4' },
    { name: 'EV9', type: 'SUV / 4x4' },
    { name: 'Rio', type: 'Citadine' },
    { name: 'Cerato', type: 'Berline' },
    { name: 'Optima', type: 'Berline' },
    { name: 'Carens', type: 'Monospace' },
    { name: 'K 2500', type: 'Utilitaire' },
    { name: 'Sorento', type: 'SUV / 4x4' },
    { name: 'Quoris', type: 'Berline' },
    { name: 'Sportage', type: 'SUV / 4x4' },
    { name: 'Picanto', type: 'Citadine' },
    { name: "Cee'd", type: 'Berline' },
    { name: 'K2700', type: 'Utilitaire' },
  ],
  LADA: [
    { name: 'Urban', type: 'SUV / 4x4' },
  ],
  LANCIA: [
    { name: 'Ypsilon', type: 'Citadine' },
  ],
  'LAND ROVER': [
    { name: 'Range Rover Sport', type: 'SUV / 4x4' },
    { name: 'Range Rover Velar', type: 'SUV / 4x4' },
    { name: 'Discovery Sport', type: 'SUV / 4x4' },
    { name: 'Defender', type: 'SUV / 4x4' },
    { name: 'Range Rover Evoque', type: 'SUV / 4x4' },
  ],
  MAHINDRA: [
    { name: 'XUV 300', type: 'SUV / 4x4' },
    { name: 'XUV 500', type: 'SUV / 4x4' },
    { name: 'KUV 100', type: 'SUV / 4x4' },
    { name: 'Pick-Up SC', type: 'Utilitaire' },
    { name: 'Pick-Up DC', type: 'Utilitaire' },
  ],
  MAZDA: [
    { name: 'Mazda 2', type: 'Citadine' },
    { name: 'Mazda 3', type: 'Berline' },
    { name: 'Mazda 6', type: 'Berline' },
    { name: 'Mazda CX5', type: 'SUV / 4x4' },
    { name: 'Mazda CX7', type: 'SUV / 4x4' },
    { name: 'Mazda CX9', type: 'SUV / 4x4' },
    { name: 'Mazda BT-50', type: 'Utilitaire' },
  ],
  'MERCEDES-BENZ': [
    { name: 'Classe A', type: 'Berline' },
    { name: 'Classe B', type: 'Monospace' },
    { name: 'Classe C', type: 'Berline' },
    { name: 'Classe CL', type: 'Coupé / Cabriolet' },
    { name: 'Classe GL', type: 'SUV / 4x4' },
    { name: 'Classe E', type: 'Berline' },
    { name: 'Classe G', type: 'SUV / 4x4' },
    { name: 'Classe X', type: 'Utilitaire' },
    { name: 'Sprinter', type: 'Utilitaire' },
    { name: 'Vito', type: 'Utilitaire' },
    { name: 'Citan', type: 'Utilitaire' },
  ],
  MG: [
    { name: 'MG 5', type: 'Berline' },
    { name: 'MG 6', type: 'Berline' },
    { name: 'MG HS', type: 'SUV / 4x4' },
    { name: 'MG EHS', type: 'SUV / 4x4' },
    { name: 'MG RX8', type: 'SUV / 4x4' },
    { name: 'MG MARVEL R', type: 'SUV / 4x4' },
    { name: 'MG RX5', type: 'SUV / 4x4' },
    { name: 'MG ONE', type: 'SUV / 4x4' },
    { name: 'MG E-ZS', type: 'SUV / 4x4' },
    { name: 'MG 3 Hybride', type: 'Citadine' },
    { name: 'MG 4', type: 'Berline' },
    { name: 'MG GT', type: 'Berline' },
    { name: 'MG 3', type: 'Citadine' },
    { name: 'MG GS', type: 'SUV / 4x4' },
    { name: 'MG ZS', type: 'SUV / 4x4' },
  ],
  MINI: [
    { name: '3 portes', type: 'Citadine' },
    { name: '5 portes', type: 'Citadine' },
    { name: 'Countryman', type: 'SUV / 4x4' },
  ],
  MITSUBISHI: [
    { name: 'Eclipse Cross', type: 'SUV / 4x4' },
    { name: 'L200', type: 'Utilitaire' },
    { name: 'Lancer', type: 'Berline' },
    { name: 'Mirage', type: 'Citadine' },
    { name: 'Attrage', type: 'Berline' },
    { name: 'ASX', type: 'SUV / 4x4' },
    { name: 'Pajero', type: 'SUV / 4x4' },
  ],
  NISSAN: [
    { name: 'Micra', type: 'Citadine' },
    { name: 'Sunny', type: 'Berline' },
    { name: 'Juke', type: 'SUV / 4x4' },
    { name: 'X-trail', type: 'SUV / 4x4' },
    { name: 'Qashqai', type: 'SUV / 4x4' },
    { name: 'Navara', type: 'Utilitaire' },
  ],
  OPEL: [
    { name: 'Adam', type: 'Citadine' },
    { name: 'Crossland', type: 'SUV / 4x4' },
    { name: 'Mokka', type: 'SUV / 4x4' },
    { name: 'Combo Cargo', type: 'Utilitaire' },
    { name: 'Corsa', type: 'Citadine' },
    { name: 'Insignia', type: 'Berline' },
    { name: 'Astra', type: 'Berline' },
    { name: 'Meriva', type: 'Monospace' },
    { name: 'Zafira', type: 'Monospace' },
  ],
  PEUGEOT: [
    { name: '107', type: 'Citadine' },
    { name: '108', type: 'Citadine' },
    { name: 'Landtrek SC', type: 'Utilitaire' },
    { name: 'Landtrek dC', type: 'Utilitaire' },
    { name: 'Rifter', type: 'Utilitaire' },
    { name: 'Bipper', type: 'Utilitaire' },
    { name: 'Partner', type: 'Utilitaire' },
    { name: '206', type: 'Citadine' },
    { name: '206 Plus', type: 'Citadine' },
    { name: '207', type: 'Citadine' },
    { name: '208', type: 'Citadine' },
    { name: 'Expert', type: 'Utilitaire' },
    { name: 'Expert Combi', type: 'Monospace' },
    { name: '301', type: 'Berline' },
    { name: '308', type: 'Berline' },
    { name: '407', type: 'Berline' },
    { name: '508', type: 'Berline' },
    { name: '1007', type: 'Citadine' },
    { name: '2008', type: 'SUV / 4x4' },
    { name: '3008', type: 'SUV / 4x4' },
    { name: '4008', type: 'SUV / 4x4' },
    { name: '5008', type: 'SUV / 4x4' },
    { name: 'Boxer', type: 'Utilitaire' },
    { name: 'RCZ', type: 'Coupé / Cabriolet' },
    { name: 'Traveller', type: 'Monospace' },
  ],
  PORSCHE: [
    { name: 'Taycan', type: 'Berline' },
    { name: 'Boxster', type: 'Coupé / Cabriolet' },
    { name: 'Cayman', type: 'Coupé / Cabriolet' },
    { name: 'Cayenne', type: 'SUV / 4x4' },
    { name: '911', type: 'Coupé / Cabriolet' },
    { name: 'Panamera', type: 'Berline' },
    { name: 'Macan', type: 'SUV / 4x4' },
  ],
  RENAULT: [
    { name: 'Arkana', type: 'SUV / 4x4' },
    { name: 'Express Van', type: 'Utilitaire' },
    { name: 'Espace', type: 'Monospace' },
    { name: 'Kwid', type: 'Citadine' },
    { name: 'Master', type: 'Utilitaire' },
    { name: 'Austral', type: 'SUV / 4x4' },
    { name: 'Symbol', type: 'Berline' },
    { name: 'Clio', type: 'Citadine' },
    { name: 'Captur', type: 'SUV / 4x4' },
    { name: 'Talisman', type: 'Berline' },
    { name: 'Fluence', type: 'Berline' },
    { name: 'Koleos', type: 'SUV / 4x4' },
    { name: 'Trafic', type: 'Utilitaire' },
    { name: 'Twingo', type: 'Citadine' },
    { name: 'Megane', type: 'Berline' },
    { name: 'Megane Sedan', type: 'Berline' },
    { name: 'Scénic', type: 'Monospace' },
    { name: 'Laguna', type: 'Berline' },
    { name: 'Kadjar', type: 'SUV / 4x4' },
  ],
  SEAT: [
    { name: 'Ateca', type: 'SUV / 4x4' },
    { name: 'Tarraco', type: 'SUV / 4x4' },
    { name: 'Ibiza', type: 'Citadine' },
    { name: 'Leon', type: 'Berline' },
    { name: 'Altea', type: 'Monospace' },
    { name: 'Toledo', type: 'Berline' },
    { name: 'Arona', type: 'SUV / 4x4' },
    { name: 'Mii', type: 'Citadine' },
  ],
  SKODA: [
    { name: 'Scala', type: 'Berline' },
    { name: 'Kamiq', type: 'SUV / 4x4' },
    { name: 'Karoq', type: 'SUV / 4x4' },
    { name: 'Superb', type: 'Berline' },
    { name: 'Kushaq', type: 'SUV / 4x4' },
    { name: 'Kodiaq', type: 'SUV / 4x4' },
    { name: 'Fabia', type: 'Citadine' },
    { name: 'Rapid', type: 'Berline' },
    { name: 'Octavia', type: 'Berline' },
    { name: 'Yeti', type: 'SUV / 4x4' },
  ],
  SOUEAST: [
    { name: 'DX3', type: 'SUV / 4x4' },
    { name: 'DX7', type: 'SUV / 4x4' },
  ],
  SSANGYONG: [
    { name: 'Musso', type: 'Utilitaire' },
    { name: 'Tivoli', type: 'SUV / 4x4' },
    { name: 'Korando', type: 'SUV / 4x4' },
    { name: 'Actyon Sports', type: 'Utilitaire' },
    { name: 'Rexton', type: 'SUV / 4x4' },
  ],
  SUZUKI: [
    { name: 'Ertiga', type: 'Monospace' },
    { name: 'S-Presso', type: 'Citadine' },
    { name: 'Jimny', type: 'SUV / 4x4' },
    { name: 'Dzire', type: 'Berline' },
    { name: 'Vitara', type: 'SUV / 4x4' },
    { name: 'Swift', type: 'Citadine' },
    { name: 'Baleno', type: 'Citadine' },
    { name: 'Ciaz', type: 'Berline' },
    { name: 'Celerio', type: 'Citadine' },
  ],
  TATA: [
    { name: 'Xenon X2 SC', type: 'Utilitaire' },
    { name: 'Xenon X2 DC', type: 'Utilitaire' },
    { name: 'Super Ace SC', type: 'Utilitaire' },
  ],
  TOYOTA: [
    { name: 'Agya', type: 'Citadine' },
    { name: 'Aygo', type: 'Citadine' },
    { name: 'Auris', type: 'Berline' },
    { name: 'C-HR', type: 'SUV / 4x4' },
    { name: 'Yaris Cross', type: 'SUV / 4x4' },
    { name: 'Yaris Sedan', type: 'Berline' },
    { name: 'Prado', type: 'SUV / 4x4' },
    { name: 'Prius', type: 'Berline' },
    { name: 'Land Cruiser', type: 'SUV / 4x4' },
    { name: 'Hiace', type: 'Utilitaire' },
    { name: 'Yaris', type: 'Citadine' },
    { name: 'Avanza', type: 'Monospace' },
    { name: 'Corolla', type: 'Berline' },
    { name: 'Corolla Sedan', type: 'Berline' },
    { name: 'Rav 4', type: 'SUV / 4x4' },
    { name: 'Hilux', type: 'Utilitaire' },
  ],
  VOLKSWAGEN: [
    { name: 'Beetle', type: 'Coupé / Cabriolet' },
    { name: 'Tiguan', type: 'SUV / 4x4' },
    { name: 'T-cross', type: 'SUV / 4x4' },
    { name: 'T-roc', type: 'SUV / 4x4' },
    { name: 'Virtus', type: 'Berline' },
    { name: 'Touran', type: 'Monospace' },
    { name: 'Transporter', type: 'Utilitaire' },
    { name: 'Crafter', type: 'Utilitaire' },
    { name: 'Caddy', type: 'Utilitaire' },
    { name: 'Golf 6', type: 'Berline' },
    { name: 'Golf 7', type: 'Berline' },
    { name: 'Golf 8', type: 'Berline' },
    { name: 'Sharan', type: 'Monospace' },
    { name: 'Jetta', type: 'Berline' },
    { name: 'Amarok', type: 'Utilitaire' },
    { name: 'Up', type: 'Citadine' },
    { name: 'Passat', type: 'Berline' },
    { name: 'Polo', type: 'Citadine' },
    { name: 'Polo Sedan', type: 'Berline' },
    { name: 'Scirocco', type: 'Coupé / Cabriolet' },
  ],
  VOLVO: [
    { name: 'S60', type: 'Berline' },
    { name: 'S90', type: 'Berline' },
    { name: 'XC40', type: 'SUV / 4x4' },
    { name: 'XC60', type: 'SUV / 4x4' },
    { name: 'XC90', type: 'SUV / 4x4' },
    { name: 'EX30', type: 'SUV / 4x4' },
    { name: 'EX90', type: 'SUV / 4x4' },
  ],
  WALLYS: [
    { name: 'Wallys 719', type: 'Citadine' },
    { name: 'Wallys 619', type: 'Citadine' },
    { name: 'Wallys IRIS', type: 'Citadine' },
    { name: 'Wallys 216', type: 'Utilitaire' },
    { name: 'Wolf', type: 'SUV / 4x4' },
  ],
  ZXAUTO: [
    { name: 'Grand Tiger', type: 'Utilitaire' },
    { name: 'Terra Lord', type: 'Utilitaire' },
  ],
  Autre: [{ name: 'Autre', type: 'Autre' }],
};
// --- Fin de la base de données de voitures ---

// Extraire les marques des clés de la base de données
const carBrands = Object.keys(carDatabase);

const fuelTypes = ['Essence', 'Diesel', 'Hybride', 'Électrique', 'GPL'];
const transmissions = ['Manuelle', 'Automatique'];
const years = Array.from({ length: 2024 - 2010 + 1 }, (_, i) => 2010 + i);

const loadingMessages = [
  'Analyse du marché...',
  'Collecte des données...',
  'Évaluation des prix en cours...',
  'Analyse des tendances actuelles...',
  'Calcul du prix estimé...',
  'Ajustement selon l\'année et le modèle...',
  'Finalisation de l\'estimation...',
];

const typingMessages = [
  "Analyse des caractéristiques du véhicule...",
  "Comparaison avec notre base de données...",
  "Évaluation des conditions du marché...",
  "Calcul de la valeur estimée...",
  "Notre IA évalue chaque détail de votre véhicule"
];

// Interface for estimation limit tracking
interface EstimationLimit {
  count: number;
  lastReset: number;
  userId: string;
}

export default function AIEstimator() {
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    mileage: '',
    fuelType: '',
    vehicleType: '',
    transmission: '',
    horsepower: '',
  });

  const [availableModels, setAvailableModels] = useState<
    Array<{ name: string; type: string }>
  >([]);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [result, setResult] = useState<EstimationResult | null>(null);
  const [error, setError] = useState('');
  
  // New states for estimation limit
  const [limitReached, setLimitReached] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState(0);
  const [estimationsRemaining, setEstimationsRemaining] = useState(3);
  
  // Move hook to top level of component
  const typingText = useTypingEffect(typingMessages);

  // Check estimation limit on component mount
  useEffect(() => {
    const checkEstimationLimit = async () => {
      // Get user identifier (IP + browser fingerprint)
      const userIP = await getUserIP();
      const browserFingerprint = await generateBrowserFingerprint();
      const userId = `${userIP}-${browserFingerprint}`;
      
      // Get current date at midnight for reset
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Get stored limit data from localStorage
      const storedLimit = localStorage.getItem('estimationLimit');
      let limitData: EstimationLimit;
      
      if (storedLimit) {
        limitData = JSON.parse(storedLimit);
        
        // Check if it's a new day or same user
        if (limitData.userId !== userId || new Date(limitData.lastReset) < today) {
          // Reset for new day or different user
          limitData = {
            count: 0,
            lastReset: today.getTime(),
            userId
          };
          localStorage.setItem('estimationLimit', JSON.stringify(limitData));
        }
      } else {
        // First time using the estimator
        limitData = {
          count: 0,
          lastReset: today.getTime(),
          userId
        };
        localStorage.setItem('estimationLimit', JSON.stringify(limitData));
      }
      
      // Check if limit is reached
      if (limitData.count >= 3) {
        setLimitReached(true);
        const timeLeft = Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
        setTimeUntilReset(timeLeft);
        setEstimationsRemaining(0);
      } else {
        setEstimationsRemaining(3 - limitData.count);
      }
    };
    
    checkEstimationLimit();
    
    // Set up timer to update time until reset
    const timer = setInterval(() => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const timeLeft = Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
      
      if (timeLeft <= 0) {
        // Reset at midnight
        setLimitReached(false);
        setEstimationsRemaining(3);
        setTimeUntilReset(0);
      } else {
        setTimeUntilReset(timeLeft);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const savedResult = sessionStorage.getItem('estimationResult');
    const savedFormData = sessionStorage.getItem('estimatorFormData');
    const useFallback = sessionStorage.getItem('useFallback');
    
    if (savedResult && savedFormData) {
      try {
        const result = JSON.parse(savedResult);
        const formData = JSON.parse(savedFormData);
        
        console.log('Received result from home page:', result);
        console.log('Received form data from home page:', formData);
        
        const completeData = {
          brand: formData.brand || '',
          model: formData.model || '',
          year: formData.year || '',
          mileage: formData.mileage || '',
          fuelType: formData.fuelType || '',
          vehicleType: formData.vehicleType || '',
          transmission: formData.transmission || '',
          horsepower: formData.horsepower || '',
        };
        
        setFormData(completeData);
        setResult(result);
        
        if (useFallback === 'true') {
          setError("Le service d'estimation est actuellement surchargé. Utilisation d'une estimation de secours.");
          sessionStorage.removeItem('useFallback');
        }
        
        sessionStorage.removeItem('estimationResult');
        sessionStorage.removeItem('estimatorFormData');
      } catch (error) {
        console.error('Error parsing saved data:', error);
      }
    }
  }, []);

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brand = e.target.value;
    const models = carDatabase[brand] || [];
    setAvailableModels(models);
    setFormData({
      ...formData,
      brand: brand,
      model: '',
      vehicleType: '',
    });
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const modelName = e.target.value;
    const selectedModel = availableModels.find((m) => m.name === modelName);
    const vehicleType = selectedModel ? selectedModel.type : '';
    setFormData({
      ...formData,
      model: modelName,
      vehicleType: vehicleType,
    });
  };

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    setContactSuccess(false);

    try {
      if (!contactForm.name || !contactForm.phone) {
        setContactError('Le nom et le numéro de téléphone sont obligatoires');
        setContactSubmitting(false);
        return;
      }

      const response = await fetch('/api/send-whatsapp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientName: contactForm.name,
          clientEmail: contactForm.email,
          clientPhone: contactForm.phone,
          appointmentTime: new Date().toISOString(),
          isCustomRequest: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setContactSuccess(true);
        setContactForm({
          name: '',
          email: '',
          phone: '',
        });
        setTimeout(() => {
          setShowContactModal(false);
          setContactSuccess(false);
        }, 2000);
      } else {
        setContactError(data.error || 'Une erreur est survenue lors de l\'envoi du message');
      }
    } catch (err) {
      console.error('Erreur lors de l\'envoi du formulaire de contact:', err);
      setContactError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Check if limit is reached
    if (limitReached) {
      return;
    }
    
    setLoading(true);
    setError('');
    setResult(null);
    let messageIndex = 0;
    setLoadingMessage(loadingMessages[0]);
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 1500);
    
    try {
      // Update estimation count
      const userIP = await getUserIP();
      const browserFingerprint = await generateBrowserFingerprint();
      const userId = `${userIP}-${browserFingerprint}`;
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Get current limit data
      const storedLimit = localStorage.getItem('estimationLimit');
      let limitData: EstimationLimit;
      
      if (storedLimit) {
        limitData = JSON.parse(storedLimit);
      } else {
        limitData = {
          count: 0,
          lastReset: today.getTime(),
          userId
        };
      }
      
      // Increment count
      limitData.count++;
      limitData.lastReset = today.getTime();
      limitData.userId = userId;
      
      // Save updated data
      localStorage.setItem('estimationLimit', JSON.stringify(limitData));
      
      // Update remaining estimations
      const remaining = 3 - limitData.count;
      setEstimationsRemaining(remaining);
      
      // Check if limit is now reached
      if (remaining <= 0) {
        setLimitReached(true);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const timeLeft = Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
        setTimeUntilReset(timeLeft);
      }
      
      const estimationResult = await estimatorApi.getEstimation(formData as EstimatorFormData);
      clearInterval(messageInterval);
      setResult(estimationResult);
    } catch (err) {
      clearInterval(messageInterval);
      console.error('Estimation error:', err);
      
      const fallbackResult = estimatorApi.getFallbackEstimation(formData as EstimatorFormData);
      setResult(fallbackResult);
      setError("Le service d'estimation est actuellement surchargé. Utilisation d'une estimation de secours.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setError('');
    setFormData({
      brand: '',
      model: '',
      year: '',
      mileage: '',
      fuelType: '',
      vehicleType: '',
      transmission: '',
      horsepower: '',
    });
    setAvailableModels([]);
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

return (
  <div className="min-h-screen bg-gray-50">
    {/* Header Section */}
    <div
      className="relative border-b border-gray-200 bg-center bg-cover"
      style={{
        backgroundImage: "url('https://i.ibb.co/3yz7gbLt/Gemini-Generated-Image-ehbc0oehbc0oehbc.png')",
      }}
    >
      {/* Dark overlay - stronger for better contrast */}
      <div className="absolute inset-0 bg-black/60"></div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white">
        <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-1.5 rounded-full mb-6">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-semibold">
            Propulsé par l'Intelligence Artificielle
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
          Estimateur de <span className="text-[#d5462e]">Prix</span> par IA
        </h1>

        <p className="text-lg text-white/90 max-w-2xl mx-auto">
          Obtenez une estimation précise et instantanée de la valeur de votre
          véhicule basée sur les données actuelles du marché.
        </p>
      </div>
    </div>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!result && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Estimation Limit Alert */}
            {limitReached && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-amber-800">Limite d'estimations atteinte</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Vous avez atteint la limite de 3 estimations par jour. Vous pourrez effectuer une nouvelle estimation dans:
                    </p>
                    <div className="mt-2 bg-amber-100 rounded px-3 py-2 inline-block">
                      <span className="text-lg font-mono font-bold text-amber-800">
                        {formatTimeRemaining(timeUntilReset)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            

            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Brand Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Car className="w-4 h-4 text-gray-500" />
                    Marque *
                  </label>
                  <select
                    required
                    value={formData.brand}
                    onChange={handleBrandChange}
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900"
                  >
                    <option value="">Sélectionnez une marque</option>
                    {carBrands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Model Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Car className="w-4 h-4 text-gray-500" />
                    Modèle *
                  </label>
                  <select
                    required
                    value={formData.model}
                    onChange={handleModelChange}
                    disabled={!formData.brand}
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500"
                  >
                    <option value="">Sélectionnez un modèle</option>
                    {availableModels.map((model) => (
                      <option key={model.name} value={model.name}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Year Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    Année *
                  </label>
                  <select
                    required
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({ ...formData, year: e.target.value })
                    }
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900"
                  >
                    <option value="">Sélectionnez l'année</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                
{/* Mileage Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-gray-500" />
                    Kilométrage *
                  </label>
                  <select
                    required
                    value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900"
                  >
                    <option value="">Sélectionnez le kilométrage</option>
                    {/* Generates options from 5,000 to 500,000 in steps of 5,000 */}
                    {Array.from({ length: 100 }, (_, i) => (i + 1) * 5000).map((km) => (
                      <option key={km} value={km}>
                        {km.toLocaleString('fr-FR')} KM
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Transmission Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Cog className="w-4 h-4 text-gray-500" />
                    Transmission *
                  </label>
                  <select
                    required
                    value={formData.transmission}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        transmission: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900"
                  >
                    <option value="">Sélectionnez la transmission</option>
                    {transmissions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Fuel Type Select */}
                <div className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-2.5 flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-gray-500" />
                    Carburant *
                  </label>
                  <select
                    required
                    value={formData.fuelType}
                    onChange={(e) =>
                      setFormData({ ...formData, fuelType: e.target.value })
                    }
                    className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all duration-200 text-gray-900"
                  >
                    <option value="">Sélectionnez le carburant</option>
                    {fuelTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>


              </div>
              <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading || limitReached}
                  className="inline-flex items-center space-x-3 bg-[#d5462e] text-white px-10 py-4 rounded-lg font-medium text-lg hover:bg-[#c23d22] transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span>Analyse en cours...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-6 w-6" />
                      <span>Estimer la valeur</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Loading Modal with Typing Effect */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 relative">
              <div className="absolute top-4 right-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-[#d5462e] animate-spin" />
                </div>
              </div>
              
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d5462e] bg-opacity-10 rounded-full mb-4">
                  <Sparkles className="w-8 h-8 text-[#d5462e]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Estimation en cours</h3>
                <p className="text-gray-600">Notre IA analyse votre véhicule</p>
              </div>
              
              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-gray-700 font-medium min-h-[24px]">
                    {typingText}
                    <span className="inline-block w-1 h-5 bg-[#d5462e] ml-1 animate-pulse"></span>
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#d5462e] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#d5462e]"></div>
                    </div>
                    <p className="text-sm text-gray-600">Notre technologie d'intelligence artificielle analyse des milliers de points de données pour vous fournir l'estimation la plus précise.</p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#d5462e] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#d5462e]"></div>
                    </div>
                    <p className="text-sm text-gray-600">Nous comparons votre véhicule avec des modèles similaires récemment vendus pour déterminer sa valeur actuelle sur le marché.</p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#d5462e] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#d5462e]"></div>
                    </div>
                    <p className="text-sm text-gray-600">Cette estimation vous aide à prendre une décision éclairée lors de la vente ou de l'achat de votre véhicule.</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Ce processus ne prend que quelques instants</p>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-[#d5462e] animate-pulse"></div>
                    <div className="w-2 h-2 rounded-full bg-[#d5462e] animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-2 rounded-full bg-[#d5462e] animate-pulse" style={{animationDelay: '0.4s'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Erreur</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <button
              onClick={resetForm}
              className="inline-flex items-center gap-2 bg-[#d5462e] text-white px-8 py-3.5 rounded-lg font-medium hover:bg-[#c23d22] transition-colors duration-200"
            >
              <Sparkles className="w-5 h-5" />
              Réessayer
            </button>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {/* Price Result Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d5462e]/10 rounded-full mb-4">
                  <TrendingUp className="w-8 h-8 text-[#d5462e]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Estimation de la valeur</h3>
                <div className="text-4xl font-bold text-gray-900 mb-6">
                  {result.estimated_price.toLocaleString('fr-TN')}
                  <span className="text-xl ml-2 text-gray-600">TND</span>
                </div>
                
                {/* Price Range */}
                <div className="flex items-center justify-between mb-6 px-2">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-500 mb-1">Minimum</div>
                    <span className="text-2xl font-semibold text-gray-900">
                      {result.price_range.min.toLocaleString('fr-TN')}
                    </span>
                    <span className="text-sm ml-1 text-gray-600">TND</span>
                  </div>
                  <div className="flex-1 mx-6">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#d5462e] rounded-full"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-500 mb-1">Maximum</div>
                    <span className="text-2xl font-semibold text-gray-900">
                      {result.price_range.max.toLocaleString('fr-TN')}
                    </span>
                    <span className="text-sm ml-1 text-gray-600">TND</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Market Analysis Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Car className="w-5 h-5 text-gray-500" />
                Analyse du Marché
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {result.market_analysis}
              </p>
            </div>

            {/* Factors Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-5 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-gray-500" />
                Facteurs Considérés
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.factors.map((factor, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{factor}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendation Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-3">
                <Lightbulb className="w-5 h-5 text-gray-500" />
                Recommandation
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {result.recommendation}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-xs text-gray-600 text-center mb-5">
                ⚠️ Estimation basée sur le marché actuel. Les prix peuvent varier
                selon l'état exact du véhicule et la négociation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={resetForm}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 border border-gray-300"
                >
                  <Sparkles className="w-5 h-5" />
                  Nouvelle Estimation
                </button>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#d5462e] text-white rounded-lg font-medium hover:bg-[#c23d22] transition-colors duration-200"
                >
                  <MessageSquare className="w-5 h-5" />
                  Contactez-nous pour une Expertise
                </button>
              </div>
            </div>
          </div>
        )}
        {/* 🆕 NEW EXPERTISE SECTION - PASTE THIS HERE */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1">
                  <p className="text-[#1e3a8a] text-lg leading-relaxed">
                    <span className="font-bold">Expertise Professionnelle:</span> Faites diagnostiquer
                    le véhicule par nos experts avant votre achat
                    pour une tranquillité d'esprit totale.
                  </p>
                </div>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold py-3 px-8 rounded-full transition-colors shadow-sm whitespace-nowrap"
                >
                  Demander une expertise
                </button>
              </div>
            </div>
        {/* Contact Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 relative">
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d5462e]/10 rounded-full mb-4">
                  <MessageSquare className="w-8 h-8 text-[#d5462e]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Demande d'Expertise
                </h3>
                <p className="text-gray-600">
                  Remplissez ce formulaire et nous vous contacterons rapidement
                </p>
              </div>

              {contactSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    Message envoyé avec succès!
                  </h4>
                  <p className="text-gray-600">
                    Nous vous contacterons dans les plus brefs délais.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  {contactError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {contactError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, name: e.target.value })
                      }
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all text-gray-900"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      Email (optionnel)
                    </label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, email: e.target.value })
                      }
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all text-gray-900"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      Numéro de téléphone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={contactForm.phone}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, phone: e.target.value })
                      }
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d5462e]/20 focus:border-[#d5462e] outline-none transition-all text-gray-900"
                      placeholder="+216 XX XXX XXX"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="w-full bg-[#d5462e] text-white px-6 py-4 rounded-lg font-medium hover:bg-[#c23d22] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
                  >
                    {contactSubmitting ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-6 h-6" />
                        <span>Envoyer la demande</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}